"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ValidateManifestDriver = void 0;
const tslib_1 = require("tslib");
const lib_1 = require("@feathersjs/hooks/lib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const lodash_1 = require("lodash");
const os_1 = require("os");
const path_1 = tslib_1.__importDefault(require("path"));
const semver_1 = tslib_1.__importDefault(require("semver"));
const typedi_1 = require("typedi");
const constants_1 = require("../../../common/constants");
const localizeUtils_1 = require("../../../common/localizeUtils");
const common_1 = require("../../../error/common");
const constant_1 = require("../../configManager/constant");
const common_2 = require("../../utils/common");
const addStartAndEndTelemetry_1 = require("../middleware/addStartAndEndTelemetry");
const wrapUtil_1 = require("../util/wrapUtil");
const errors_1 = require("./errors");
const results_1 = require("./results");
const CopilotGptManifestUtils_1 = require("./utils/CopilotGptManifestUtils");
const ManifestUtils_1 = require("./utils/ManifestUtils");
const telemetry_1 = require("./utils/telemetry");
const actionName = "teamsApp/validateManifest";
let ValidateManifestDriver = class ValidateManifestDriver {
    constructor() {
        this.description = (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.description.validateDriver");
        this.progressTitle = (0, localizeUtils_1.getLocalizedString)("plugins.appstudio.validateManifest.progressBar.message");
    }
    async execute(args, context) {
        const wrapContext = new wrapUtil_1.WrapDriverContext(context, actionName, actionName);
        const res = await this.validate(args, wrapContext);
        return {
            result: res,
            summaries: wrapContext.summaries,
        };
    }
    async validate(args, context) {
        var _a, _b, _c, _d, _e, _f, _g;
        const result = this.validateArgs(args);
        if (result.isErr()) {
            return (0, teamsfx_api_1.err)(result.error);
        }
        const manifestPath = (0, common_2.getAbsolutePath)(args.manifestPath, context.projectPath);
        const manifestRes = await ManifestUtils_1.manifestUtils.getManifestV3(manifestPath, context);
        if (manifestRes.isErr()) {
            return (0, teamsfx_api_1.err)(manifestRes.error);
        }
        const manifest = manifestRes.value;
        let manifestValidationResult;
        const telemetryProperties = {};
        if (manifest.$schema) {
            try {
                manifestValidationResult = await teamsfx_api_1.ManifestUtil.validateManifest(manifest);
                telemetryProperties[telemetry_1.TelemetryPropertyKey.validationErrors] = manifestValidationResult
                    .map((r) => r.replace(/\//g, ""))
                    .join(";");
            }
            catch (e) {
                return (0, teamsfx_api_1.err)(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.ValidationFailedError.name, errors_1.AppStudioError.ValidationFailedError.message([
                    (0, localizeUtils_1.getLocalizedString)("error.appstudio.validateFetchSchemaFailed", manifest.$schema, e.message),
                ]), constants_1.HelpLinks.WhyNeedProvision));
            }
        }
        else {
            return (0, teamsfx_api_1.err)(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.ValidationFailedError.name, errors_1.AppStudioError.ValidationFailedError.message([
                (0, localizeUtils_1.getLocalizedString)("error.appstudio.validateSchemaNotDefined"),
            ]), constants_1.HelpLinks.WhyNeedProvision));
        }
        // validate localization files
        const localizationFilesValidationRes = await this.validateLocalizatoinFiles(args, context, manifest);
        if (localizationFilesValidationRes.isErr()) {
            return (0, teamsfx_api_1.err)(localizationFilesValidationRes.error);
        }
        telemetryProperties[telemetry_1.TelemetryPropertyKey.localizationValidationErrors] =
            localizationFilesValidationRes.value.error.map((r) => r.replace(/\//g, "")).join(";");
        const manifestVersion = manifest.manifestVersion === "devPreview"
            ? semver_1.default.coerce("1.19.0") // for MetaOS WXP, fallback the `devPreview` ver as `1.19.0` to enable following logics
            : semver_1.default.coerce(manifest.manifestVersion); // ensure manifestVersion is a valid semver
        let declarativeAgents;
        if (manifestVersion && semver_1.default.gte(manifestVersion, "1.19.0")) {
            declarativeAgents = (_a = manifest.copilotAgents) === null || _a === void 0 ? void 0 : _a.declarativeAgents;
        }
        // Declarative Copilot
        let declarativeAgentValidationResult;
        if ((declarativeAgents === null || declarativeAgents === void 0 ? void 0 : declarativeAgents.length) && declarativeAgents[0].file) {
            const declarativeCopilotPath = path_1.default.join(path_1.default.dirname(manifestPath), declarativeAgents[0].file);
            const declarativeCopilotValidationRes = await CopilotGptManifestUtils_1.copilotGptManifestUtils.validateAgainstSchema(declarativeAgents[0], declarativeCopilotPath, context);
            if (declarativeCopilotValidationRes.isErr()) {
                return (0, teamsfx_api_1.err)(declarativeCopilotValidationRes.error);
            }
            else {
                declarativeAgentValidationResult = declarativeCopilotValidationRes.value;
                telemetryProperties[telemetry_1.TelemetryPropertyKey.gptValidationErrors] =
                    declarativeAgentValidationResult === null || declarativeAgentValidationResult === void 0 ? void 0 : declarativeAgentValidationResult.validationResult.map((r) => r.replace(/\//g, "")).join(";");
                if (declarativeAgentValidationResult.actionValidationResult.length > 0) {
                    let errors = [];
                    for (let index = 0; index < declarativeAgentValidationResult.actionValidationResult.length; index++) {
                        errors = errors.concat(declarativeAgentValidationResult.actionValidationResult[index].validationResult.map((r) => index.toString() + ":" + r.replace(/\//g, "")));
                    }
                    telemetryProperties[`${telemetry_1.TelemetryPropertyKey.gptActionValidationErrors}`] =
                        errors.join(";");
                }
            }
        }
        const actionErrorCount = (_b = declarativeAgentValidationResult === null || declarativeAgentValidationResult === void 0 ? void 0 : declarativeAgentValidationResult.actionValidationResult.filter((o) => o.filePath !== "").reduce((acc, { validationResult }) => acc + validationResult.length, 0)) !== null && _b !== void 0 ? _b : 0;
        const allErrorCount = manifestValidationResult.length +
            localizationFilesValidationRes.value.error.length +
            ((_c = declarativeAgentValidationResult === null || declarativeAgentValidationResult === void 0 ? void 0 : declarativeAgentValidationResult.validationResult.length) !== null && _c !== void 0 ? _c : 0) +
            actionErrorCount;
        if (allErrorCount > 0) {
            const summaryStr = (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.validate.failed", allErrorCount);
            if (context.platform === teamsfx_api_1.Platform.CLI) {
                const outputMessage = [
                    {
                        content: "Microsoft 365 Agents Toolkit has checked manifest(s) with corresponding schema:\n\nSummary: \n",
                        color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                    },
                    {
                        content: `${allErrorCount} failed.\n`,
                        color: teamsfx_api_1.Colors.BRIGHT_RED,
                    },
                ];
                if (manifestValidationResult.length > 0) {
                    outputMessage.push({
                        content: (0, localizeUtils_1.getDefaultString)("driver.teamsApp.summary.validateTeamsManifest.checkPath", args.manifestPath) + "\n",
                        color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                    });
                    manifestValidationResult.map((error) => {
                        outputMessage.push({ content: `${constant_1.SummaryConstant.Failed} `, color: teamsfx_api_1.Colors.BRIGHT_RED });
                        outputMessage.push({
                            content: `${error}\n`,
                            color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                        });
                    });
                }
                if (localizationFilesValidationRes.value.error.length > 0) {
                    outputMessage.push({
                        content: (0, localizeUtils_1.getDefaultString)("driver.teamsApp.summary.validateTeamsManifest.checkPath", localizationFilesValidationRes.value.filePath) + "\n",
                        color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                    });
                    localizationFilesValidationRes.value.error.map((error) => {
                        outputMessage.push({ content: `${constant_1.SummaryConstant.Failed} `, color: teamsfx_api_1.Colors.BRIGHT_RED });
                        outputMessage.push({
                            content: `${error}\n`,
                            color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                        });
                    });
                }
                if (declarativeAgentValidationResult) {
                    const validationMessage = CopilotGptManifestUtils_1.copilotGptManifestUtils.logValidationErrors(declarativeAgentValidationResult, context.platform);
                    if (validationMessage) {
                        outputMessage.push(...validationMessage);
                    }
                }
                (_d = context.ui) === null || _d === void 0 ? void 0 : _d.showMessage("info", outputMessage, false);
            }
            else {
                // logs in output window
                const teamsManifestErrors = manifestValidationResult
                    .map((error) => {
                    return `${constant_1.SummaryConstant.Failed} ${error}`;
                })
                    .join(os_1.EOL);
                let outputMessage = os_1.EOL + (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.validateManifest", summaryStr);
                if (teamsManifestErrors.length > 0) {
                    outputMessage +=
                        os_1.EOL +
                            (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.validateTeamsManifest.checkPath", args.manifestPath) +
                            os_1.EOL +
                            teamsManifestErrors;
                }
                if (localizationFilesValidationRes.value.error.length > 0) {
                    const localizationErrors = localizationFilesValidationRes.value.error
                        .map((error) => {
                        return `${constant_1.SummaryConstant.Failed} ${error}`;
                    })
                        .join(os_1.EOL);
                    outputMessage +=
                        os_1.EOL +
                            (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.validateTeamsManifest.checkPath", localizationFilesValidationRes.value.filePath) +
                            os_1.EOL +
                            localizationErrors;
                }
                if (declarativeAgentValidationResult) {
                    const validationMessage = CopilotGptManifestUtils_1.copilotGptManifestUtils.logValidationErrors(declarativeAgentValidationResult, context.platform);
                    if (validationMessage) {
                        outputMessage += os_1.EOL + validationMessage;
                    }
                }
                outputMessage += os_1.EOL;
                (_e = context.logProvider) === null || _e === void 0 ? void 0 : _e.info(outputMessage);
            }
            (0, lodash_1.merge)(context.telemetryProperties, telemetryProperties);
            return (0, teamsfx_api_1.err)(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.ValidationFailedError.name, [
                (0, localizeUtils_1.getDefaultString)("driver.teamsApp.validate.result", summaryStr),
                (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.validate.result.display", summaryStr),
            ]));
        }
        else {
            // logs in output window
            const summaryStr = (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.validate.succeed", (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.validate.all"));
            const outputMessage = os_1.EOL + (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.validateManifest", summaryStr, "", "");
            (_f = context.logProvider) === null || _f === void 0 ? void 0 : _f.info(outputMessage);
            const validationSuccess = (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.validate.result.display", summaryStr);
            if (context.platform === teamsfx_api_1.Platform.VS) {
                context.logProvider.info(validationSuccess);
            }
            if (args.showMessage) {
                if (context.platform === teamsfx_api_1.Platform.CLI) {
                    const outputMessage = [
                        {
                            content: "Microsoft 365 Agents Toolkit has completed checking your app package against validation rules. " +
                                summaryStr +
                                ".",
                            color: teamsfx_api_1.Colors.BRIGHT_GREEN,
                        },
                    ];
                    context.logProvider.info(outputMessage);
                }
                else {
                    (_g = context.ui) === null || _g === void 0 ? void 0 : _g.showMessage("info", validationSuccess, false);
                }
            }
            return (0, teamsfx_api_1.ok)(new Map());
        }
    }
    validateArgs(args) {
        if (!args || !args.manifestPath) {
            return (0, teamsfx_api_1.err)(new common_1.InvalidActionInputError(actionName, ["manifestPath"], "https://aka.ms/teamsfx-actions/teamsapp-validate"));
        }
        return (0, teamsfx_api_1.ok)(undefined);
    }
    async validateLocalizatoinFiles(args, context, manifest) {
        var _a, _b, _c, _d, _e;
        const manifestVersion = semver_1.default.coerce(manifest.manifestVersion);
        let additionalLanguages;
        if (manifestVersion && semver_1.default.gte(manifestVersion, "1.5.0")) {
            additionalLanguages = (_a = manifest.localizationInfo) === null || _a === void 0 ? void 0 : _a.additionalLanguages;
        }
        let defaultLanguageFile;
        let defaultLanguageTag;
        if (manifestVersion && semver_1.default.gte(manifestVersion, "1.19.0")) {
            defaultLanguageTag = (_b = manifest.localizationInfo) === null || _b === void 0 ? void 0 : _b.defaultLanguageTag;
            defaultLanguageFile = (_c = manifest.localizationInfo) === null || _c === void 0 ? void 0 : _c.defaultLanguageFile;
        }
        if ((additionalLanguages === null || additionalLanguages === void 0 ? void 0 : additionalLanguages.length) == 0 && !defaultLanguageFile) {
            return (0, teamsfx_api_1.ok)({ error: [] });
        }
        const languageList = additionalLanguages || [];
        if (defaultLanguageFile && defaultLanguageTag) {
            languageList.push({
                languageTag: defaultLanguageTag,
                file: defaultLanguageFile,
            });
        }
        for (const language of languageList) {
            const filePath = language === null || language === void 0 ? void 0 : language.file;
            if (!filePath) {
                return (0, teamsfx_api_1.err)(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.ValidationFailedError.name, errors_1.AppStudioError.ValidationFailedError.message([
                    (0, localizeUtils_1.getLocalizedString)("error.appstudio.localizationFile.pathNotDefined", filePath),
                ])));
            }
            const localizationFileDir = path_1.default.dirname((0, common_2.getAbsolutePath)(args.manifestPath, context.projectPath));
            const localizationFilePath = (0, common_2.getAbsolutePath)(filePath, localizationFileDir);
            const resolvedLocFileRes = await ManifestUtils_1.manifestUtils.resolveLocFile(localizationFilePath);
            if (resolvedLocFileRes.isErr()) {
                return (0, teamsfx_api_1.err)(resolvedLocFileRes.error);
            }
            const localizationFile = JSON.parse(resolvedLocFileRes.value);
            try {
                const schema = await teamsfx_api_1.ManifestUtil.fetchSchema(localizationFile);
                // the current localization schema has invalid regex sytax, we need to manually fix the properties temporarily
                const activityDespString = "^activities.activityTypes\\[\\b([0-9]|[1-8][0-9]|9[0-9]|1[01][0-9]|12[0-7])\\b]\\.description$";
                const fixedActivityDespString = "^activities.activityTypes\\[\\b([0-9]|[1-8][0-9]|9[0-9]|1[01][0-9]|12[0-7])\\b\\]\\.description$";
                if ((_d = schema.patternProperties) === null || _d === void 0 ? void 0 : _d[activityDespString]) {
                    schema.patternProperties[fixedActivityDespString] =
                        schema.patternProperties[activityDespString];
                    delete schema.patternProperties[activityDespString];
                }
                const activityTemplateString = "^activities.activityTypes\\[\\b([0-9]|[1-8][0-9]|9[0-9]|1[01][0-9]|12[0-7])\\b]\\.templateText$";
                const fixedActivityTemplateString = "^activities.activityTypes\\[\\b([0-9]|[1-8][0-9]|9[0-9]|1[01][0-9]|12[0-7])\\b\\]\\.templateText$";
                if ((_e = schema.patternProperties) === null || _e === void 0 ? void 0 : _e[activityTemplateString]) {
                    schema.patternProperties[fixedActivityTemplateString] =
                        schema.patternProperties[activityTemplateString];
                    delete schema.patternProperties[activityTemplateString];
                }
                const validationRes = await teamsfx_api_1.ManifestUtil.validateManifestAgainstSchema(localizationFile, schema);
                if (validationRes.length > 0) {
                    return (0, teamsfx_api_1.ok)({ error: validationRes, filePath: localizationFilePath });
                }
            }
            catch (e) {
                return (0, teamsfx_api_1.err)(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.ValidationFailedError.name, errors_1.AppStudioError.ValidationFailedError.message([
                    (0, localizeUtils_1.getLocalizedString)("error.appstudio.localizationFile.validationException", filePath, e.message),
                ])));
            }
        }
        return (0, teamsfx_api_1.ok)({ error: [] });
    }
};
tslib_1.__decorate([
    (0, lib_1.hooks)([(0, addStartAndEndTelemetry_1.addStartAndEndTelemetry)(actionName, actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, wrapUtil_1.WrapDriverContext]),
    tslib_1.__metadata("design:returntype", Promise)
], ValidateManifestDriver.prototype, "validate", null);
ValidateManifestDriver = tslib_1.__decorate([
    (0, typedi_1.Service)(actionName)
], ValidateManifestDriver);
exports.ValidateManifestDriver = ValidateManifestDriver;
//# sourceMappingURL=validate.js.map